import React, { useState, useEffect, ChangeEvent } from 'react';
import type { FC } from 'react';
import { gameService } from '../firebase/gameService';
import type { GameState, Player } from '../types/game';
import finalRoundMusic from '../musicandsounds/finalround.mp3';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBolt, FaTrophy } from 'react-icons/fa';

interface FinalJeopardyProps {
  gameId: string;
  game: GameState;
  onComplete: () => void;
  readOnly?: boolean;
}

// WinnersPodium component
function WinnersPodium({ players }: { players: Player[] }) {
  // Sort players by score descending
  const sorted = [...players].sort((a, b) => b.score - a.score).slice(0, 3);
  const podiumStyles = [
    {
      bg: 'from-[#FFD700]/90 to-[#FFB800]/90',
      border: 'border-yellow-400',
      text: 'text-yellow-900',
      shadow: 'shadow-yellow-300',
      medal: '🥇',
      place: 1,
      scale: 1.15,
      z: 20,
    },
    {
      bg: 'from-[#e0e0e0]/90 to-[#bdbdbd]/90',
      border: 'border-gray-300',
      text: 'text-gray-700',
      shadow: 'shadow-gray-300',
      medal: '🥈',
      place: 2,
      scale: 1,
      z: 10,
    },
    {
      bg: 'from-[#b87333]/90 to-[#a0522d]/90',
      border: 'border-orange-900',
      text: 'text-orange-900',
      shadow: 'shadow-orange-900',
      medal: '🥉',
      place: 3,
      scale: 1,
      z: 10,
    },
  ];
  return (
    <div className="flex flex-col items-center mt-16">
      {/* Confetti/Sparkle effect */}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="mb-4 flex items-center gap-3"
      >
        <FaTrophy className="text-3xl text-brand-yellow drop-shadow animate-bounce" />
        <span className="text-4xl font-bold text-brand-yellow drop-shadow-lg">Winners Podium</span>
        <FaTrophy className="text-3xl text-brand-yellow drop-shadow animate-bounce" />
      </motion.div>
      {/* Podium Cards */}
      <div className="flex flex-row justify-center items-end gap-8 w-full max-w-4xl mt-8 flex-wrap">
        {sorted.map((player, idx) => {
          const style = podiumStyles[idx] || podiumStyles[2];
          return (
            <motion.div
              key={player.id}
              initial={{ opacity: 0, y: 60, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: style.scale }}
              transition={{ duration: 0.7 + idx * 0.2, type: 'spring', bounce: 0.4 }}
              className={`relative flex flex-col items-center bg-gradient-to-br ${style.bg} border-4 ${style.border} rounded-3xl px-10 py-12 min-w-[220px] max-w-xs w-full ${style.shadow}`}
              style={{ zIndex: style.z, boxShadow: idx === 0 ? '0 0 60px 0 rgba(255,215,64,0.25)' : undefined }}
            >
              {/* Glowing Avatar */}
              <div className="w-28 h-28 rounded-full border-4 border-white mb-4 bg-white flex items-center justify-center overflow-hidden shadow-lg" style={{ boxShadow: idx === 0 ? '0 0 32px 0 #FFD70088' : undefined }}>
                <img
                  src={player.avatar ? require(`../images/${player.avatar}`) : require('../images/default-avatar.png')}
                  alt={player.name}
                  className="w-24 h-24 object-cover"
                />
              </div>
              <span className={`font-extrabold text-2xl mb-2 ${style.text}`}>{player.name}</span>
              <span className={`text-xl mb-2 ${style.text}`}>Score: <span className="font-bold text-black">${player.score}</span></span>
              {/* Medal and Place */}
              <div className="flex flex-col items-center mt-2">
                <span className="text-2xl">{style.medal}</span>
                <span className="text-lg font-bold text-black/80">{style.place}</span>
              </div>
            </motion.div>
          );
        })}
      </div>
      {/* Confetti emoji for extra celebration */}
      <motion.div
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.8, duration: 0.7 }}
        className="mt-8 text-3xl"
      >
        🎉✨🎊
      </motion.div>
    </div>
  );
}

// Helper for timer color
function getTimerColor(time: number) {
  if (time > 10) return 'text-brand-yellow';
  if (time > 5) return 'text-orange-400';
  return 'text-red-500';
}

const FinalJeopardy: FC<FinalJeopardyProps> = ({ gameId, game, onComplete, readOnly = false }): JSX.Element | null => {
  const [wagerInputs, setWagerInputs] = useState<{ [playerId: string]: string }>({});
  const [answerInputs, setAnswerInputs] = useState<{ [playerId: string]: string }>({});
  const [allWagersSubmitted, setAllWagersSubmitted] = useState(false);
  const [timerActive, setTimerActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(30);
  const [timerFinished, setTimerFinished] = useState(false);
  const [answerRevealed, setAnswerRevealed] = useState(false);
  const [showPodium, setShowPodium] = useState(false);
  const [music, setMusic] = useState<HTMLAudioElement | null>(null);

  // Timer effect: only for host, updates timeRemaining in Firebase for all
  useEffect(() => {
    if (!game.finalJeopardy) return;
    if (!timerActive) return;
    if (readOnly) return; // Only host updates timer
    let timer: NodeJS.Timeout;
    if (game.finalJeopardy.isRevealed && game.finalJeopardy.timeRemaining > 0) {
      timer = setInterval(async () => {
        const newTime = (game.finalJeopardy?.timeRemaining ?? 30) - 1;
        await gameService.updateGame(gameId, {
          finalJeopardy: {
            ...game.finalJeopardy!,
            timeRemaining: newTime
          }
        });
        setTimeRemaining(newTime);
        if (newTime <= 0) {
          setTimerActive(false);
          setTimerFinished(true);
        }
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [timerActive, game.finalJeopardy, readOnly, gameId]);

  // Handle wager submission
  const handleWagerSubmit = async (playerId: string) => {
    const amount = parseInt(wagerInputs[playerId]);
    if (isNaN(amount)) return;
    
    const player = game.players.find(p => p.id === playerId);
    if (!player || player.role !== 'player') return; // Only handle on-stage players

    const currentWagers = game.finalJeopardy?.wagers || {};
    await gameService.updateGame(gameId, {
      finalJeopardy: {
        ...game.finalJeopardy!,
        wagers: {
          ...currentWagers,
          [playerId]: {
            ...currentWagers[playerId],
            amount
          }
        }
      }
    });
  };

  // Handle answer submission
  const handleAnswerSubmit = async (playerId: string) => {
    const answer = answerInputs[playerId];
    if (!answer) return;

    const player = game.players.find(p => p.id === playerId);
    if (!player || player.role !== 'player') return; // Only handle on-stage players

    const currentWagers = game.finalJeopardy?.wagers || {};
    await gameService.updateGame(gameId, {
      finalJeopardy: {
        ...game.finalJeopardy!,
        wagers: {
          ...currentWagers,
          [playerId]: {
            ...currentWagers[playerId],
            answer
          }
        }
      }
    });
  };

  // Check if all wagers are submitted
  useEffect(() => {
    if (!game.finalJeopardy) return;
    const onStagePlayers = game.players.filter(p => p.role === 'player');
    const allSubmitted = onStagePlayers.every(
      p => game.finalJeopardy?.wagers?.[p.id]?.amount !== undefined
    );
    setAllWagersSubmitted(allSubmitted);
  }, [game.finalJeopardy, game.players]);

  // Start Final Jeopardy: reveal question and start timer
  const handleStartFinalJeopardy = async () => {
    setTimerActive(true);
    setTimeRemaining(30);
    setTimerFinished(false);
    await gameService.updateGame(gameId, {
      finalJeopardy: {
        ...game.finalJeopardy!,
        isRevealed: true,
        timeRemaining: 30
      }
    });
    // Play music
    const audio = new Audio(finalRoundMusic);
    audio.play();
    setMusic(audio);
  };

  // Host marks answer as correct/incorrect
  const handleScoreUpdate = async (playerId: string, isCorrect: boolean) => {
    const player = game.players.find(p => p.id === playerId);
    const wager = game.finalJeopardy?.wagers?.[playerId];
    if (!player || !wager) return;
    const scoreChange = isCorrect ? wager.amount : -wager.amount;
    const newScore = Math.max(0, player.score + scoreChange);
    await gameService.updateGame(gameId, {
      finalJeopardy: {
        ...game.finalJeopardy!,
        wagers: {
          ...game.finalJeopardy!.wagers,
          [playerId]: {
            ...game.finalJeopardy!.wagers[playerId],
            isCorrect
          }
        }
      },
      players: game.players.map(p => 
        p.id === playerId ? { ...p, score: newScore } : p
      )
    });
  };

  // Complete Final Jeopardy
  const completeFinalJeopardy = async () => {
    await gameService.updateGame(gameId, {
      status: 'finished',
      finalJeopardy: {
        ...game.finalJeopardy!,
        isAnswered: true
      }
    });
    setShowPodium(true);
    onComplete();
  };

  // Stop music when timer ends or round is completed
  useEffect(() => {
    if (timerFinished && music) {
      music.pause();
      music.currentTime = 0;
    }
  }, [timerFinished, music]);

  useEffect(() => {
    return () => {
      if (music) {
        music.pause();
        music.currentTime = 0;
      }
    };
  }, [music]);

  if (!game.finalJeopardy) return null;

  // Show podium for host after ending, or for audience when game is finished
  if (
    showPodium ||
    (readOnly && (game.status === 'finished' || game.finalJeopardy?.isAnswered))
  ) {
    return <WinnersPodium players={game.players.filter(p => p.role === 'player')} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-[#181818] to-black flex items-center justify-center p-8 relative overflow-hidden">
      {/* Subtle vignette effect */}
      <div className="absolute inset-0 pointer-events-none" style={{background: 'radial-gradient(ellipse at center, rgba(255,215,64,0.08) 0%,rgba(0,0,0,0.95) 80%)'}} />
      <div className="relative z-10 w-full max-w-3xl mx-auto">
        {/* Final Jeopardy Label */}
        <motion.div
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="flex justify-center mb-4"
        >
          <div className="flex items-center gap-3 bg-gradient-to-r from-[#FFD700] via-[#FFB800] to-[#FFD700] text-black px-8 py-3 rounded-t-2xl shadow-lg uppercase tracking-widest font-extrabold text-lg border-b-4 border-brand-yellow drop-shadow-lg">
            <FaBolt className="text-xl text-yellow-500 animate-pulse" />
            Final Jeopardy
            <FaBolt className="text-xl text-yellow-500 animate-pulse" />
          </div>
        </motion.div>
        {/* Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="bg-[#181818] border-4 border-brand-yellow rounded-3xl shadow-2xl px-10 py-12 flex flex-col items-center relative"
          style={{ boxShadow: '0 0 60px 0 rgba(255,215,64,0.15)' }}
        >
          {/* Category */}
          <div className="mb-6">
            <span className="uppercase tracking-widest text-sm font-semibold text-brand-yellow/80 block text-center mb-1">Category</span>
            <span className="text-3xl md:text-4xl font-extrabold text-brand-yellow text-center block drop-shadow-lg">{game.finalJeopardy.category}</span>
          </div>
          {/* Question */}
          <AnimatePresence>
            {game.finalJeopardy.isRevealed && !timerFinished && (
              <motion.div
                key="question"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.7 }}
                className="mb-10"
              >
                <div className="text-white text-2xl md:text-3xl font-bold text-center max-w-2xl mx-auto drop-shadow-lg animate-fade-in">
                  {game.finalJeopardy.question}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          {/* Timer */}
          <AnimatePresence>
            {game.finalJeopardy.isRevealed && !timerFinished && (
              <motion.div
                key="timer"
                initial={{ scale: 0.7, opacity: 0 }}
                animate={{ scale: 1.1, opacity: 1 }}
                exit={{ scale: 0.7, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20, duration: 0.5 }}
                className="flex flex-col items-center"
              >
                <div className={`font-extrabold text-5xl md:text-6xl ${getTimerColor(game.finalJeopardy.timeRemaining ?? timeRemaining)} drop-shadow-lg transition-colors duration-300 animate-pulse`}
                  style={{ letterSpacing: '0.05em' }}
                >
                  {game.finalJeopardy.timeRemaining ?? timeRemaining}
                  <span className="text-2xl md:text-3xl font-bold ml-1">s</span>
                </div>
                <div className="mt-2 text-brand-yellow/70 text-sm tracking-widest uppercase">Time Remaining</div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
        {/* Step 1: Enter wagers */}
        {(!allWagersSubmitted || readOnly) && (
          <div className="mb-8">
            <h3 className="text-white text-xl mb-6 text-center">Enter wagers for each player:</h3>
            <div className="flex flex-row justify-center items-end gap-8 flex-wrap">
              {game.players
                .filter(player => player.role === 'player') // Only show on-stage players
                .map((player: Player) => {
                  const wager = game.finalJeopardy?.wagers?.[player.id];
                  return (
                    <div
                      key={player.id}
                      className="bg-gradient-to-br from-[#232526] to-[#414345] border-2 border-brand-yellow/60 rounded-2xl p-5 flex flex-col items-center shadow-2xl min-w-[220px] max-w-[240px] mx-2"
                      style={{ minHeight: 270 }}
                    >
                      <img
                        src={player.avatar ? require(`../images/${player.avatar}`) : require('../images/default-avatar.png')}
                        alt={player.name}
                        className="w-28 h-28 rounded-full border-4 border-brand-yellow mb-3 object-cover shadow-lg"
                      />
                      <span className="text-brand-yellow font-extrabold text-2xl mb-1 tracking-wide drop-shadow">{player.name}</span>
                      <span className="text-white text-lg mb-4 font-semibold">Score: <span className="font-bold text-brand-yellow">${player.score}</span></span>
                      {!wager?.amount && (
                        <div className="w-full">
                          <input
                            type="number"
                            value={wagerInputs[player.id] || ''}
                            onChange={(e) => setWagerInputs({ ...wagerInputs, [player.id]: e.target.value })}
                            placeholder="Enter wager"
                            className="w-full p-2 rounded-lg bg-[#333] text-white border border-brand-yellow/40 focus:border-brand-yellow focus:outline-none mb-2"
                            min="0"
                            max={player.score}
                          />
                          <button
                            onClick={() => handleWagerSubmit(player.id)}
                            disabled={!wagerInputs[player.id]}
                            className="w-full bg-brand-yellow text-black font-bold px-4 py-2 rounded-lg hover:bg-[#fccd6c]/90 transition-colors disabled:opacity-50"
                          >
                            Submit Wager
                          </button>
                        </div>
                      )}
                      {wager?.amount && !wager.answer && game.finalJeopardy?.isRevealed && (
                        <div className="w-full">
                          <input
                            type="text"
                            value={answerInputs[player.id] || ''}
                            onChange={(e) => setAnswerInputs({ ...answerInputs, [player.id]: e.target.value })}
                            placeholder="Enter your answer"
                            className="w-full p-2 rounded-lg bg-[#333] text-white border border-brand-yellow/40 focus:border-brand-yellow focus:outline-none mb-2"
                          />
                          <button
                            onClick={() => handleAnswerSubmit(player.id)}
                            disabled={!answerInputs[player.id]}
                            className="w-full bg-brand-yellow text-black font-bold px-4 py-2 rounded-lg hover:bg-[#fccd6c]/90 transition-colors disabled:opacity-50"
                          >
                            Submit Answer
                          </button>
                        </div>
                      )}
                      {wager?.amount && (
                        <div className="text-white text-lg font-semibold">
                          Wager: <span className="font-bold text-brand-yellow">${wager.amount}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Step 2: Reveal Question and Start Timer */}
        {allWagersSubmitted && !game.finalJeopardy.isRevealed && !readOnly && (
          <div className="text-center mb-8">
            <button
              onClick={handleStartFinalJeopardy}
              className="bg-brand-yellow text-black font-bold px-8 py-4 rounded-xl shadow-lg hover:bg-[#fccd6c]/90 transition-colors text-xl"
            >
              Reveal Question
            </button>
          </div>
        )}

        {/* Step 4: Reveal answer button after timer */}
        {game.finalJeopardy.isRevealed && timerFinished && !answerRevealed && (
          <div className="text-center mb-8">
            <button
              onClick={() => setAnswerRevealed(true)}
              className="bg-brand-yellow text-black font-bold px-8 py-4 rounded-xl shadow-lg hover:bg-[#fccd6c]/90 transition-colors text-xl"
            >
              Reveal Answer
            </button>
          </div>
        )}

        {/* Step 5: Show answer and allow marking answers */}
        {answerRevealed && (
          <>
            {/* Styled Answer Reveal */}
            <div className="text-center mb-12">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                className="inline-block px-8 py-4 rounded-2xl bg-gradient-to-r from-[#FFD700]/80 via-[#FFB800]/90 to-[#FFD700]/80 shadow-2xl border-4 border-brand-yellow mb-4"
                style={{ boxShadow: '0 0 40px 0 rgba(255,215,64,0.25)' }}
              >
                <span className="text-2xl md:text-3xl font-extrabold text-black tracking-wide drop-shadow-lg">Answer:</span>
                <span className="ml-4 text-2xl md:text-3xl font-extrabold text-brand-yellow drop-shadow-lg animate-pulse">{game.finalJeopardy.answer}</span>
              </motion.div>
            </div>
            {/* Player Score Cards */}
            <div className="flex flex-wrap justify-center gap-8 mb-8">
              {game.players.filter(p => p.role === 'player').map(player => {
                const wager = game.finalJeopardy?.wagers?.[player.id];
                const isCorrect = wager?.isCorrect;
                return (
                  <motion.div
                    key={player.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className={`relative flex flex-col items-center bg-gradient-to-br from-[#181818] to-[#232323] border-4 rounded-2xl shadow-2xl px-8 py-8 min-w-[240px] max-w-xs w-full ${isCorrect === true ? 'border-green-400/80' : isCorrect === false ? 'border-red-400/80' : 'border-brand-yellow/80'}`}
                    style={{ boxShadow: isCorrect !== undefined ? (isCorrect ? '0 0 32px 0 rgba(34,197,94,0.18)' : '0 0 32px 0 rgba(239,68,68,0.18)') : '0 0 32px 0 rgba(255,215,64,0.12)' }}
                  >
                    <div className="flex flex-col items-center mb-4">
                      <div className="w-20 h-20 rounded-full border-4 border-brand-yellow mb-2 bg-[#232323] flex items-center justify-center overflow-hidden">
                        <img
                          src={player.avatar ? require(`../images/${player.avatar}`) : require('../images/default-avatar.png')}
                          alt={player.name}
                          className="w-16 h-16 object-cover"
                        />
                      </div>
                      <span className="text-brand-yellow font-extrabold text-xl mb-1 tracking-wide drop-shadow">{player.name}</span>
                      <span className="text-white text-lg mb-1 font-semibold">Wager: <span className="font-bold text-brand-yellow">${wager?.amount ?? 0}</span></span>
                      <span className="text-white text-lg font-semibold">Score: <span className="font-bold text-brand-yellow">${player.score}</span></span>
                      {wager?.answer && (
                        <div className="text-white text-lg mt-2">
                          Answer: <span className="font-bold text-brand-yellow">{wager.answer}</span>
                        </div>
                      )}
                    </div>
                    {/* Correct/Incorrect Buttons or Result */}
                    {!readOnly && (
                      <div className="flex gap-4 w-full mt-4">
                        <motion.button
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleScoreUpdate(player.id, true)}
                          className={`flex-1 px-0 py-0 rounded-lg font-bold text-2xl h-12 transition-all ${wager?.isCorrect === true ? 'bg-green-600 text-white scale-110 shadow-lg' : 'bg-green-700/80 text-white hover:bg-green-600/90'}`}
                          disabled={wager?.isCorrect !== undefined}
                        >
                          ✔️
                        </motion.button>
                        <motion.button
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleScoreUpdate(player.id, false)}
                          className={`flex-1 px-0 py-0 rounded-lg font-bold text-2xl h-12 transition-all ${wager?.isCorrect === false ? 'bg-red-600 text-white scale-110 shadow-lg' : 'bg-red-700/80 text-white hover:bg-red-600/90'}`}
                          disabled={wager?.isCorrect !== undefined}
                        >
                          ❌
                        </motion.button>
                      </div>
                    )}
                    {/* Animated Result Icon */}
                    {wager?.isCorrect !== undefined && (
                      <motion.div
                        initial={{ scale: 0.7, opacity: 0 }}
                        animate={{ scale: 1.2, opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 18, delay: 0.1 }}
                        className="absolute top-2 right-2"
                      >
                        {wager.isCorrect ? (
                          <span className="text-green-400 text-3xl drop-shadow-lg">✔️</span>
                        ) : (
                          <span className="text-red-400 text-3xl drop-shadow-lg">❌</span>
                        )}
                      </motion.div>
                    )}
                  </motion.div>
                );
              })}
            </div>
            {/* End Game Button */}
            {game.players
              .filter(p => p.role === 'player')
              .every(p => game.finalJeopardy?.wagers?.[p.id]?.isCorrect !== undefined) && (
              <div className="text-center mt-8">
                <button
                  onClick={completeFinalJeopardy}
                  className="bg-brand-yellow text-black font-bold px-8 py-4 rounded-xl shadow-lg hover:bg-[#fccd6c]/90 transition-colors text-xl"
                >
                  End Game
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FinalJeopardy; 